import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { action, computed, observable, makeObservable, runInAction } from 'mobx';
import { FleetType } from '@bringg/dashboard-sdk/dist/DeliveryCatalog/DeliveryCatalog.consts';
import { CarrierCancellationReason, ReasonToCancelTask } from '@bringg/types';

import notification from 'bringg-web/services/notification';
import { CARRIER_CANCELLATION_REASON_MAPPING_FF } from '../../components/delivery-catalog-connect/consts';

class CarrierCancellationReasonMappingStore {
	carrierCancellationReasons: CarrierCancellationReason[] = [];
	fdmReasons: ReasonToCancelTask[] = [];
	savedMappings: Map<number, number> = new Map();
	stagedMappings: Map<number, number> = new Map();
	isFetching = false;
	isSaving = false;
	private _fetchSeq = 0;

	constructor() {
		makeObservable(this, {
			carrierCancellationReasons: observable.shallow,
			fdmReasons: observable.shallow,
			savedMappings: observable.shallow,
			stagedMappings: observable.shallow,
			isFetching: observable,
			isSaving: observable,
			isEnabled: computed,
			isDirty: computed,
			unmappedCount: computed,
			setMapping: action,
			discardMappings: action
		});
	}

	// eslint-disable-next-line class-methods-use-this
	get isEnabled(): boolean {
		return !!getRootEnv().dashboardSdk.sdk.session?.merchant?.feature_flags?.[
			CARRIER_CANCELLATION_REASON_MAPPING_FF
		];
	}

	get isDirty(): boolean {
		if (this.stagedMappings.size !== this.savedMappings.size) {
			return true;
		}
		for (const [id, fdmId] of this.stagedMappings) {
			if (this.savedMappings.get(id) !== fdmId) {
				return true;
			}
		}
		return false;
	}

	get unmappedCount(): number {
		return this.carrierCancellationReasons.filter(r => !this.stagedMappings.has(r.id)).length;
	}

	setMapping = (carrierId: number, fdmId: number) => {
		this.stagedMappings = new Map([...this.stagedMappings, [carrierId, fdmId]]);
	};

	discardMappings = () => {
		this.stagedMappings = new Map(this.savedMappings);
	};

	fetchReasons = async (fleetType: FleetType) => {
		const seq = ++this._fetchSeq;
		runInAction(() => {
			this.isFetching = true;
			this.carrierCancellationReasons = [];
			this.fdmReasons = [];
			this.savedMappings = new Map();
			this.stagedMappings = new Map();
		});
		try {
			const { carrierCancellationReasons, carrierCancellationReasonMappings, reasonToCancelTasks } =
				getRootEnv().dashboardSdk.sdk;
			const [reasons, mappings, fdmReasons] = await Promise.all([
				carrierCancellationReasons.getAll(fleetType),
				carrierCancellationReasonMappings.getAll(fleetType),
				reasonToCancelTasks.getAll()
			]);
			if (seq !== this._fetchSeq) return;
			const savedMap = new Map<number, number>(
				mappings.map((m: { carrier_cancellation_reason_id: number; reason_to_cancel_task_id: number }) => [
					m.carrier_cancellation_reason_id,
					m.reason_to_cancel_task_id
				])
			);
			runInAction(() => {
				this.carrierCancellationReasons = reasons;
				this.fdmReasons = fdmReasons;
				this.savedMappings = savedMap;
				this.stagedMappings = new Map(savedMap);
			});
		} catch {
			if (seq !== this._fetchSeq) return;
			notification.error('DELIVERY_PROVIDER_CATALOG_CONNECT.CANCELLATION_REASONS.FETCH_ERROR');
		} finally {
			if (seq === this._fetchSeq) {
				runInAction(() => {
					this.isFetching = false;
				});
			}
		}
	};

	saveMappings = async (fleetType: FleetType) => {
		if (!this.isDirty) return;
		runInAction(() => {
			this.isSaving = true;
		});
		try {
			const mappings = Array.from(this.stagedMappings.entries()).map(([carrierId, fdmId]) => ({
				carrier_cancellation_reason_id: carrierId,
				reason_to_cancel_task_id: fdmId
			}));
			await getRootEnv().dashboardSdk.sdk.carrierCancellationReasonMappings.batchUpdate(fleetType, mappings);
			runInAction(() => {
				this.savedMappings = new Map(this.stagedMappings);
			});
		} catch {
			notification.error('DELIVERY_PROVIDER_CATALOG_CONNECT.CANCELLATION_REASONS.SAVE_ERROR');
		} finally {
			runInAction(() => {
				this.isSaving = false;
			});
		}
	};
}

export default CarrierCancellationReasonMappingStore;
